import React, { useState, useEffect, useCallback } from 'react';
import { portfolioImages } from '../data/mock';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('concerts');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const categories = [
  { id: 'concerts', label: 'Concerts' },
  { id: 'portraits', label: 'Portraits/Headshots' },
  { id: 'childrenPets', label: 'Children & Pets' },
  { id: 'sportsDance', label: 'Sports & Dance' },
  { id: 'weddings', label: 'Weddings' },
  { id: 'all', label: 'All' }];

  const categoryLabels = Object.fromEntries(categories.map((c) => [c.id, c.label]));

  const getFilteredImages = () => {
    if (activeCategory === 'all') {
      return [
      ...portfolioImages.concerts,
      ...portfolioImages.portraits,
      ...portfolioImages.childrenPets,
      ...portfolioImages.sportsDance,
      ...portfolioImages.weddings];
    }
    return portfolioImages[activeCategory] || [];
  };

  const filteredImages = getFilteredImages();

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((i) => (i - 1 + filteredImages.length) % filteredImages.length);
  }, [filteredImages.length]);

  const goNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((i) => (i + 1) % filteredImages.length);
  }, [filteredImages.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, goPrev, goNext]);

  const lightboxImage = lightboxIndex !== null ? filteredImages[lightboxIndex] : null;

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-wider text-gray-500 mb-2">My Work</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Portfolio</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A curated collection showcasing the energy of live music and the artistry of every moment captured
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-6 py-2 rounded-full font-medium transition-all transform hover:scale-105 ${
            activeCategory === category.id ?
            'bg-pink-500 text-white shadow-lg' :
            'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
            }>
              {category.label}
            </button>
          )}
        </div>

        {/* Image Grid */}
        {filteredImages.length > 0 ?
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image, index) =>
          <div
            key={image.id}
            className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105"
            onClick={() => openLightbox(index)}>

                <div className="aspect-w-1 aspect-h-1 relative h-80">
                  <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false} />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-xs uppercase tracking-wider mb-1">{categoryLabels[image.category] || image.category}</p>
                  <h4 className="text-lg font-bold">{image.title}</h4>
                </div>
              </div>
          )}

            {/* Copyright Notice */}
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg border-t-4 border-pink-500 flex flex-col justify-center h-80">
              <p className="font-bold text-gray-900 mb-3">
                &copy; All photographs are copyright by Portraits by Neill (a.k.a. Diane Neill).
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                Absolutely NO Copying, Downloading, Printing or Reuse of ANY photographs in any way without WRITTEN permission from the Owner, (PBN). These rules are protected by copyright and punishable by law!
              </p>
            </div>
          </div> :

        <div className="text-center py-20">
            <p className="!text-xl mb-4 text-gray-500"></p>
            <p className="text-gray-400"></p>
          </div>
        }
      </div>

      {/* Lightbox */}
      {lightboxImage &&
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={closeLightbox}>

          <div className="relative max-w-6xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
            src={lightboxImage.url}
            alt={lightboxImage.title}
            className="max-w-full max-h-[90vh] object-contain"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false} />

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
              <p className="text-sm uppercase tracking-wider mb-1">{lightboxImage.category}</p>
              <h3 className="text-2xl font-bold">{lightboxImage.title}</h3>
              <p className="text-sm text-gray-300 mt-1">{lightboxIndex + 1} / {filteredImages.length}</p>
            </div>

            {/* Close button */}
            <button
            className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 leading-none"
            onClick={closeLightbox}>
              ×
            </button>

            {/* Prev button */}
            <button
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full ml-[-16px] bg-black/50 hover:bg-black/80 text-white w-12 h-12 flex items-center justify-center rounded-full transition-colors"
            onClick={goPrev}>
              &#8592;
            </button>

            {/* Next button */}
            <button
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full mr-[-16px] bg-black/50 hover:bg-black/80 text-white w-12 h-12 flex items-center justify-center rounded-full transition-colors"
            onClick={goNext}>
              &#8594;
            </button>
          </div>
        </div>
      }
    </section>);
};

export default Portfolio;
